// =====================================================================
// RelatoriosDiarios (S39) — pagina /relatorios-diarios no CRM.
// Lista os relatorios diarios gerados pelo cron (06h BRT), permite
// abrir HTML completo em modal, baixar como arquivo, e regenerar
// manualmente (admin/super).
// =====================================================================
import React, { useState, useEffect, useContext, useMemo } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Paper,
  Grid,
  Button,
  CircularProgress,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Box
} from "@material-ui/core";
import RefreshIcon from "@material-ui/icons/Refresh";
import AddIcon from "@material-ui/icons/Add";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import { AuthContext } from "../../context/Auth/AuthContext";
import { toast } from "react-toastify";

import {
  listDailyReports,
  generateDailyReport
} from "../../services/dailyReportsService";

import ReportCard from "./components/ReportCard";
import ReportViewerModal from "./components/ReportViewerModal";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "auto",
    minHeight: 0
  },
  toolbar: {
    display: "flex",
    gap: theme.spacing(2),
    alignItems: "center",
    flexWrap: "wrap",
    marginBottom: theme.spacing(2)
  },
  monthSelect: { flexGrow: 1, minWidth: 130 },
  emptyState: {
    padding: theme.spacing(4),
    textAlign: "center",
    color: theme.palette.text.secondary
  },
  loadingWrap: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: 200
  }
}));

// Gera lista de meses (atual + 11 anteriores)
const buildMonthOptions = () => {
  const opts = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const months = [
      "Janeiro",
      "Fevereiro",
      "Marco",
      "Abril",
      "Maio",
      "Junho",
      "Julho",
      "Agosto",
      "Setembro",
      "Outubro",
      "Novembro",
      "Dezembro"
    ];
    opts.push({
      value: `${y}-${m}`,
      label: `${months[d.getMonth()]} ${y}`
    });
  }
  return opts;
};

const todayISO = () => {
  const now = new Date();
  // BRT = UTC-3h
  const brt = new Date(now.getTime() - 3 * 60 * 60 * 1000);
  return brt.toISOString().slice(0, 10);
};

const yesterdayISO = () => {
  const now = new Date();
  const brt = new Date(now.getTime() - 3 * 60 * 60 * 1000);
  brt.setUTCDate(brt.getUTCDate() - 1);
  return brt.toISOString().slice(0, 10);
};

const RelatoriosDiarios = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);

  const monthOptions = useMemo(() => buildMonthOptions(), []);
  const [selectedMonth, setSelectedMonth] = useState(monthOptions[0].value);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [generateDate, setGenerateDate] = useState(yesterdayISO());

  const canRegenerate = user.profile === "admin" || user.super;

  const loadReports = async () => {
    setLoading(true);
    try {
      const list = await listDailyReports({ month: selectedMonth });
      setReports(list);
    } catch (err) {
      console.error("Erro ao carregar relatorios:", err);
      toast.error("Erro ao carregar relatorios diarios.");
      setReports([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMonth]);

  const handleGenerate = async () => {
    if (!generateDate || !/^\d{4}-\d{2}-\d{2}$/.test(generateDate)) {
      toast.error("Data invalida.");
      return;
    }
    setGenerating(true);
    try {
      const result = await generateDailyReport({
        date: generateDate,
        force: true
      });
      if (result.status === "success") {
        toast.success(
          `Relatorio gerado (id=${result.reportId}). Custo: US$ ${(result.cost || 0).toFixed(4)}.`
        );
      } else {
        toast.warn(
          `Relatorio gerado com status ${result.status}. ${result.error || ""}`
        );
      }
      await loadReports();
    } catch (err) {
      console.error("Erro ao gerar:", err);
      toast.error("Erro ao gerar relatorio. Veja logs do servidor.");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <MainContainer>
      <MainHeader>
        <Title>Relatorios Diarios</Title>
        <MainHeaderButtonsWrapper>
          <Button
            size="small"
            startIcon={<RefreshIcon />}
            onClick={loadReports}
            disabled={loading}
          >
            Atualizar
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper variant="outlined" className={classes.mainPaper}>
        <Box className={classes.toolbar}>
          <FormControl
            variant="outlined"
            size="small"
            className={classes.monthSelect}
          >
            <InputLabel>Mes</InputLabel>
            <Select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              label="Mes"
            >
              {monthOptions.map((m) => (
                <MenuItem key={m.value} value={m.value}>
                  {m.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {canRegenerate && (
            <>
              <TextField
                size="small"
                variant="outlined"
                label="Data (YYYY-MM-DD)"
                type="date"
                value={generateDate}
                onChange={(e) => setGenerateDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                style={{ flexGrow: 1, minWidth: 150 }}
              />
              <Button
                variant="contained"
                color="primary"
                size="small"
                startIcon={<AddIcon />}
                onClick={handleGenerate}
                disabled={generating}
              >
                {generating ? "Gerando..." : "Gerar/Regenerar"}
              </Button>
            </>
          )}
        </Box>

        {loading ? (
          <Box className={classes.loadingWrap}>
            <CircularProgress />
          </Box>
        ) : reports.length === 0 ? (
          <Box className={classes.emptyState}>
            <Typography variant="body1" gutterBottom>
              Nenhum relatorio diario disponivel para esse mes.
            </Typography>
            <Typography variant="body2">
              {canRegenerate ? (
                <>
                  Use o botao <strong>Gerar/Regenerar</strong> acima pra criar
                  um relatorio manualmente.
                </>
              ) : (
                <>O cron diario gera automaticamente as 06h BRT.</>
              )}
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={2}>
            {reports.map((r) => (
              <Grid item xs={12} md={6} lg={4} key={r.id}>
                <ReportCard
                  report={r}
                  onOpen={() => setSelectedReport(r)}
                  canRegenerate={canRegenerate}
                  onRegenerate={async () => {
                    setGenerateDate(r.report_date);
                    await handleGenerate();
                  }}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </Paper>

      {selectedReport && (
        <ReportViewerModal
          reportId={selectedReport.id}
          reportDate={selectedReport.report_date}
          onClose={() => setSelectedReport(null)}
        />
      )}
    </MainContainer>
  );
};

export default RelatoriosDiarios;
